// noinspection JSUnresolvedVariable

/*
 * A messenger object encapsulating the event aggregator, commands, and request/response backbone objects
 * Marionette needed since these Backbone extensions are in Marionette's library
 * Messenger object also used for managing saves and dialog windows
 */

define(["backboneBase", "underscore", "jquery", "marionetteBase"], function (Backbone, _, $) {
  //extend RequestResponse with a removeHandlerByContext function
  const ReqRes = Backbone.Wreqr.RequestResponse.extend({
    removeHandlersByContext: function (context) {
      const keys = _.keys(this._wreqrHandlers);
      for (let i = 0; i < keys.length; i++) {
        if (this._wreqrHandlers[keys[i]].context === context) {
          //console.log("removing request " + keys[i]);
          delete this._wreqrHandlers[keys[i]];
        }
      }
    },
  });

  const BUTTONS = {
    NO: "no",
    OK: "ok",
    YES: "yes",
  };

  const Messenger = {
    BUTTONS: BUTTONS,
    //optional place to store an error message during a save
    errorMsg: null,
    vent: new Backbone.Wreqr.EventAggregator(),
    reqres: new ReqRes(),
    command: new Backbone.Wreqr.Commands(),
    getFullName: function (name, namespace) {
      if (namespace) {
        return `${namespace}:${name}`;
      }
      return name;
    },
    //function used for performing actions when a save is triggered
    onSave: function (callback, context) {
      this.vent.off("save"); //only have one callback set up for a save event
      this.vent.on("save", callback, context);
      // Also turn off the saveConflict handler, since most entries do not add their own handler
      this.vent.off("saveConflict");
    },
    onSaveConflict: function (callback, context) {
      this.vent.off("saveConflict");
      this.vent.on("saveConflict", callback, context);
    },
    //save function, which uses the vent with "save" and "save:attempted" events
    save: function (callback, context) {
      //only allow one save at a time
      if (!this.inSave) {
        //before we start the save, make sure that any textboxes are closed
        //if the textbox can't be closed, the function returns false, and we just cancel the save
        console.log("closing text box");
        if (this.reqres.request("save:textbox")) {
          this.inSave = true; //currently saving
          //clear the error message parameter
          this.errorMsg = null;
          //define callback for when save is complete
          this.vent.once("save:attempted", callback, context); //need context in order to properly dispose of callback
          // if close is called on the module
          this.vent.once(
            "save:attempted",
            function () {
              //allow saves again
              this.inSave = false;
              //close save display
              this.vent.trigger("close:saving:display");
            },
            this,
          );
          //trigger saving display
          this.vent.trigger("show:saving:display");
          this.vent.trigger("save");
        } else {
          callback.apply(context); //save failed
        }
      }
    },
    saveAttempted: function (success, hideError) {
      if (!success && !hideError) {
        const errorMsg = this.errorMsg || $.t("commonMessages.serverError");
        this.okDialog(errorMsg, {
          ok: () => {
            if (this.errorConflict) {
              this.vent.trigger("saveConflict");
            }
          },
        });
      }
      //trigger event that save has been attempted
      this.vent.trigger("save:attempted", success);
    },
    savePromise: function () {
      return new Promise((resolve, reject) => {
        this.save((success) => {
          if (success) {
            resolve();
          } else {
            reject();
          }
        });
      });
    },

    //create a dialog with a message and a yes and no button.  Callbacks object
    //includes functions to be called for 'yes' and 'no' button presses.
    //Functions cannot have arguments
    yesNoDialog: function (message, callbacks, context) {
      if (!callbacks) {
        callbacks = {};
      }
      if (!callbacks.yes) {
        callbacks.yes = _.noop;
      }
      if (!callbacks.no) {
        callbacks.no = _.noop;
      }
      if (!message) {
        message = "";
      }
      this.vent.trigger("show:dialog:window", {
        message: message,
        callbacks: callbacks,
        context: context,
      });
    },
    yesNoDialogAsync: function (message) {
      return new Promise((resolve) => {
        this.vent.trigger("show:dialog:window", {
          message: message,
          callbacks: {
            no: () => resolve(BUTTONS.NO),
            yes: () => resolve(BUTTONS.YES),
          },
        });
      });
    },
    //create a dialog with a message and an ok button.  Callbacks object
    //includes function to be called for 'ok' button presses.
    //Functions cannot have arguments
    okDialog: function (message, callbacks, context) {
      if (!callbacks) {
        callbacks = {};
      }
      if (!callbacks.ok) {
        callbacks.ok = _.noop;
      }
      if (!message) {
        message = "";
      }
      this.vent.trigger("show:dialog:window", {
        message: message,
        callbacks: callbacks,
        context: context,
      });
    },

    confirmDialog: function (
      message = "",
      callbacks = {},
      context = null,
      confirmI18NKey = "commons.ok",
      showComment = false,
    ) {
      if (!callbacks.confirm) {
        callbacks.confirm = $.noop;
      }
      if (!callbacks.cancel) {
        callbacks.cancel = $.noop;
      }
      this.vent.trigger("show:dialog:window", {
        message: message,
        callbacks: callbacks,
        context: context,
        confirmI18NKey: confirmI18NKey,
        showComment: showComment,
      });
    },
    //store an error message to be shown when the save fails
    setErrorMsgFromResponse: function (resp) {
      this.errorConflict = resp.status == 409;
      this.errorMsg = Messenger.getErrorFromResponse(resp, {
        allowArray: true,
        defaultErrorMsg: false,
      });
    },
    showErrorFromResponse: function (resp, callbacks, context) {
      const errorMsg = Messenger.getErrorFromResponse(resp, {
        allowArray: true,
        defaultErrorMsg: $.t("commonMessages.serverError"),
      });
      if (errorMsg) {
        this.okDialog(errorMsg, {
          ok: () => {
            if (resp.status == 409) {
              this.vent.trigger("saveConflict");
            }
            if (callbacks) {
              callbacks.call(context || this);
            }
          },
        });
      }
    },
    getErrorFromResponse: function (
      resp,
      { allowArray, defaultErrorMsg } = {
        allowArray: false,
        defaultErrorMsg: false,
      },
    ) {
      try {
        let errorMsg = defaultErrorMsg;
        let responseJson;
        if (resp) {
          if ("responseJSON" in resp) {
            responseJson = resp.responseJSON;
          } else if (_.isFunction(resp.json)) {
            responseJson = resp.json();
          }
          if (responseJson) {
            let jsonErrorMsg;
            if (responseJson.errors && responseJson.errors.length) {
              jsonErrorMsg = [];
              for (const error of responseJson.errors) {
                if (error.errorId) {
                  jsonErrorMsg.push($.t(error.errorId, error.errorArguments));
                } else {
                  jsonErrorMsg.push(error.errorMessage || error.error);
                }
              }
            } else if (responseJson.errorId) {
              jsonErrorMsg = $.t(responseJson.errorId, responseJson.errorArguments);
            } else if (responseJson.name == "Error") {
              jsonErrorMsg = responseJson.value;
            } else {
              jsonErrorMsg = responseJson.errorMessage || responseJson.error;
            }
            if (jsonErrorMsg) {
              errorMsg = jsonErrorMsg;
            }
          }
          if (resp instanceof Error) {
            errorMsg = resp.message;
          }
          let changeTimestamp;
          let changeUserName;
          let itemName;
          let errorId;
          switch (resp.status) {
            case 401:
              errorMsg = $.t("commonMessages.notLoggedIn");
              break;
            case 403:
              errorMsg = $.t("commonMessages.notAllowed");
              break;
            case 404:
            case 410:
              errorMsg = $.t("commonMessages.notFound");
              break;
            case 408:
            case 429:
            case 504:
            case 509:
              errorMsg = $.t("commonMessages.serverBusy");
              break;
            case 409:
              if (responseJson && responseJson.errors && responseJson.errors.length > 0) {
                // at the moment all database updates triggered by authoring tool are done one at a time. Hence, there
                // should only be one error in the list of errors.
                let error = responseJson.errors[0];
                if (error.errorArguments) {
                  changeTimestamp = error.errorArguments.changeTimestamp;
                  changeUserName = error.errorArguments.changeUserName;
                  itemName = error.errorArguments.itemName;
                }
                errorId = error.errorId;
                if (changeTimestamp) {
                  changeTimestamp = Messenger.reqres.request(
                    "get:formatted:dateTime",
                    changeTimestamp,
                  );
                }
              }
              errorMsg = $.t(errorId, {
                item: itemName || "item",
                user: changeUserName || "another user",
                time: changeTimestamp || "an unknown time",
              });
              break;
          }
        }
        if (!allowArray && Array.isArray(errorMsg)) {
          errorMsg = errorMsg.join("\n");
        }
        return errorMsg;
      } catch (e) {
        console.error("Unexpected problem extracting error from response: ", e);
        return defaultErrorMsg;
      }
    },
  };

  //set a default onSave callback in case no onSave() has been defined
  Messenger.vent.on("save", function () {
    Messenger.saveAttempted(true);
  });

  return Messenger;
});
